import client, { mock, isRealApiEnabled } from "./client";
import statsMock from "./mocks/stats";

const clone = (value) => JSON.parse(JSON.stringify(value));

/**
 * @endpoint GET /api/v1/stats/overall
 * Returns everything the Overall tab renders: the General-section total cards,
 * customer-activity series, overall category pie, and the per-category line +
 * pie data keyed by category tab.
 * @returns {Promise<{
 *   totalCards: { id: number, title: string, total: string }[],
 *   customerActivity: { activeDay: string, data: { day: string, value: number }[] },
 *   lineGraphLines: { key: string, label: string, color: string }[],
 *   overallPie: { label: string, value: number }[],
 *   categoryTabs: string[],
 *   categoryDataByKey: Record<string, {
 *     lineGraphData: object[],
 *     categoryPieData: { id: string, data: { label: string, value: number }[] }[],
 *   }>,
 * }>}
 */
export function overall(range) {
  if (isRealApiEnabled()) {
    return client.get("/v1/stats/overall", { query: { range: range || "" } });
  }
  return mock(statsMock.overall);
}

/**
 * @endpoint GET /api/v1/stats/products?q=:query
 * Type-ahead search over the product catalog for the Single Item tab. Matches
 * name, sku, creator and category. An empty query returns no rows.
 * @returns {Promise<{ id: string, name: string, sku: string, creator: string, category: string }[]>}
 */
export function searchItems(query) {
  const normalized = (query || "").trim();
  if (isRealApiEnabled()) {
    if (!normalized) return mock([]);
    return client.get("/v1/stats/products", { query: { q: normalized } });
  }
  const lowered = normalized.toLowerCase();
  if (!lowered) return mock([]);
  const matches = statsMock.singleItemCatalog.filter(
    ({ name, sku, creator, category }) => {
      const haystack = `${name} ${sku} ${creator || ""} ${
        category || ""
      }`.toLowerCase();
      return haystack.includes(lowered);
    }
  );
  return mock(matches);
}

/**
 * @endpoint GET /api/v1/stats/products/:productId
 * Per-item stats for the Single Item tab (totals, customer activity, pie).
 * @returns {Promise<{
 *   totals: { id: string, title: string, total: string }[],
 *   customerActivity: { activeDay: string, data: { day: string, value: number }[] },
 *   pie: { label: string, value: number }[],
 * }>}
 */
export function singleItem(productId) {
  if (isRealApiEnabled()) {
    return client.get(`/v1/stats/products/${encodeURIComponent(productId)}`);
  }
  return mock(statsMock.singleItem);
}

/**
 * @endpoint GET /api/v1/stats/stock-report
 * Rows for the Stock Report tab table.
 * @returns {Promise<{
 *   id: string, sku: string, bin: string, title: string, available: string,
 *   average: string, onHand: string,
 *   salesByDate: { date: string, quantity: number }[],
 * }[]>}
 */
export function stockReport() {
  if (isRealApiEnabled()) {
    return client.get("/v1/stats/stock-report");
  }
  return mock(clone(statsMock.stockReport));
}
